import { FormGroup, Validators } from '@angular/forms';
import { TrailerFormContentData } from 'app/features/yard-control/components/trailer-inventory/trailer-form/trailer-form.component';
import { concatMap, from, reduce, takeWhile } from 'rxjs';
import { CustomRegex } from 'shared/constants/custom-regex.const';
import {
  ArriveGroups,
  DepartGroups,
  TrailerDirection,
  TrailerFormAction,
  TrailerFuelLevel,
  TrailerStatus,
  TrailerType,
} from 'shared/enums';
import { CustomValidators } from 'shared/helpers/custom-validators';
import { PopupService } from 'shared/services';
import { FieldValidators, SiteCapabilities, Trailer } from 'shared/types';

interface ICheckVariants {
  fuel: (trailer: Partial<Trailer>) => string[];
  storageDepart: (trailer: Partial<Trailer>) => string[];
  storageArrive: (trailer: Partial<Trailer>) => string[];
  fuelAndStorage: (trailer: Partial<Trailer>) => string[];
}

type DialogMode = 'Arrive' | 'Edit';

/**
 * @deprecated firefly-1-9758-ui will be removed after feature flag expires
 */
export class TrailerHelper {
  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static isReefer(type?: TrailerType | null): boolean {
    if (!type) return false;
    return type === TrailerType.REEFER || type === TrailerType.CONTAINER_REEFER;
  }

  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static isLoaded(status: TrailerStatus): boolean {
    return status === TrailerStatus.LOADED || status === TrailerStatus.PARTIAL;
  }

  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static checkForWarnings(
    trailer: Partial<Trailer>,
    popupSvc: PopupService,
    checkType: keyof ICheckVariants,
    submitFn: () => void,
    mode: DialogMode = 'Arrive',
  ): void {
    const checkVariants: ICheckVariants = {
      fuel: (trailer: Partial<Trailer>) => TrailerHelper.getFuelCheck.call(null, trailer, mode),
      storageDepart: TrailerHelper.getStorageDepartCheck,
      storageArrive: TrailerHelper.getStorageArriveCheck,
      fuelAndStorage: (trailer: Partial<Trailer>) => TrailerHelper.getFuelAndStorageChecks.call(null, trailer, mode),
    };
    TrailerHelper.runCheck(popupSvc, trailer, checkVariants[checkType], submitFn);
  }

  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static setValidators(form: FormGroup, field: FieldValidators): void {
    const control = form.get(field.fieldName);
    if (!control) return;
    control.setValidators(field.validators);
    control.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static clearValidators(form: FormGroup, field: FieldValidators): void {
    const control = form.get(field.fieldName);
    if (!control) return;
    control.clearValidators();
    control.setErrors(null);
    control.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static getArriveTrailerGroup(type: TrailerType): ArriveGroups {
    switch (type) {
      case TrailerType.REEFER:
      case TrailerType.CONTAINER_REEFER:
        return ArriveGroups.REEFERS;

      case TrailerType.VAN:
      case TrailerType.CONTAINER_DRY:
      case TrailerType.OPEN_TOP:
      case TrailerType.CURTAINSIDE:
      case TrailerType.HOPPER:
        return ArriveGroups.VAN_CONT_OTOP_CURT_HOP;

      case TrailerType.TANKER:
      case TrailerType.SIDE_DUMP:
      case TrailerType.FLATBED:
        return ArriveGroups.SIDE_FLAT_TANK;

      case TrailerType.LIVESTOCK:
        return ArriveGroups.LIVE;
      case null:
      default:
        throw new Error(`Unknown trailer type ${type}`);
    }
  }

  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static getDepartTrailerGroup(type: TrailerType): DepartGroups {
    switch (type) {
      case TrailerType.REEFER:
      case TrailerType.CONTAINER_REEFER:
        return DepartGroups.REEFERS;

      case TrailerType.VAN:
      case TrailerType.CONTAINER_DRY:
      case TrailerType.OPEN_TOP:
      case TrailerType.CURTAINSIDE:
      case TrailerType.LIVESTOCK:
      case TrailerType.TANKER:
      case TrailerType.HOPPER:
        return DepartGroups.VAN_CONT_OTOP_CURT_LIVE_TANK_HOP;
      case TrailerType.SIDE_DUMP:
      case TrailerType.FLATBED:
        return DepartGroups.SIDE_FLAT;
      case null:
      default:
        throw new Error(`Unknown trailer type ${type}`);
    }
  }

  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static temperatureFields: FieldValidators[] = [
    {
      fieldName: 'setTemperature',
      validators: [
        Validators.required,
        Validators.pattern(CustomRegex.positiveAndNegativeNumbers),
        CustomValidators.customMessage(Validators.required, 'yardControl.customValidations.loadedRequiresSetTemp'),
      ],
    },
    {
      fieldName: 'boxTemperature',
      validators: [
        Validators.required,
        Validators.pattern(CustomRegex.positiveAndNegativeNumbers),
        CustomValidators.customMessage(Validators.required, 'yardControl.customValidations.loadedRequiresBoxTemp'),
      ],
    },
  ];
  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static temperatureFieldsValidations: FieldValidators[] = [
    {
      fieldName: 'setTemperature',
      validators: [Validators.pattern(CustomRegex.positiveAndNegativeNumbers)],
    },
    {
      fieldName: 'boxTemperature',
      validators: [Validators.pattern(CustomRegex.positiveAndNegativeNumbers)],
    },
  ];
  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static sealFieldsRequired: FieldValidators[] = [
    {
      fieldName: 'sealNumber',
      validators: [
        Validators.required,
        Validators.pattern(CustomRegex.alphaNumeric),
        Validators.maxLength(20),
        CustomValidators.customMessage(Validators.required, 'yardControl.customValidations.loadedRequiresSealNumber'),
      ],
    },
    {
      fieldName: 'sealIntact',
      validators: [
        CustomValidators.customMessage(
          Validators.requiredTrue,
          'yardControl.customValidations.loadedRequiresSealIntact',
        ),
      ],
    },
    {
      fieldName: 'sealMatchesBol',
      validators: [
        CustomValidators.customMessage(Validators.requiredTrue, 'yardControl.customValidations.loadedRequiresSealBol'),
      ],
    },
  ];
  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static sealFieldsOptional: FieldValidators[] = [
    {
      fieldName: 'sealNumber',
      validators: [Validators.maxLength(20), Validators.pattern(CustomRegex.alphaNumeric)],
    },
  ];
  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static shipmentFieldsRequired: FieldValidators[] = [
    {
      fieldName: 'shipmentNumber',
      validators: [
        Validators.required,
        Validators.maxLength(20),
        Validators.pattern(CustomRegex.alphaNumeric),
        CustomValidators.customMessage(Validators.required, 'yardControl.customValidations.loadedRequiresNumber'),
      ],
    },
  ];
  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static shipmentFieldsOptional: FieldValidators[] = [
    {
      fieldName: 'shipmentNumber',
      validators: [Validators.maxLength(20), Validators.pattern(CustomRegex.alphaNumeric)],
    },
  ];

  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static trailerTypeAndStatusValidations(form: FormGroup, contentData: TrailerFormContentData): void {
    const status = form.get('trailerStatus')?.value;
    const trailerType = form.get('trailerType')?.value;
    let shipmentValidations = TrailerHelper.shipmentFieldsRequired;
    let sealValidations = TrailerHelper.sealFieldsRequired;
    const temperatureValidations = TrailerHelper.temperatureFieldsValidations;

    if (!contentData.site.capabilities?.shipmentNumber) {
      shipmentValidations = TrailerHelper.shipmentFieldsOptional;
    }
    if (!contentData.site.capabilities?.sealNumber) {
      sealValidations = TrailerHelper.sealFieldsOptional;
    }

    // Clear validations for all fields
    [
      ...TrailerHelper.temperatureFields,
      ...TrailerHelper.shipmentFieldsRequired,
      ...TrailerHelper.sealFieldsRequired,
    ].forEach((field) => {
      TrailerHelper.clearValidators(form, field);
      form.get(field.fieldName)?.setErrors(null);
      form.get(field.fieldName)?.updateValueAndValidity({ emitEvent: false });
    });

    // do not reset fuel level and continuous values if we're changing between
    // reefer and container reefer, otherwise set to 1/2 and false.
    if (TrailerHelper.isReefer(trailerType)) {
      if (!form.get('fuelLevel')?.value) {
        form.get('fuelLevel')?.setValue(TrailerFuelLevel.Fuel50Percent, { emitEvent: false });
        form.get('continuous')?.setValue(false, { emitEvent: false });
      }
    } else {
      form.get('fuelLevel')?.setValue(null, { emitEvent: false });
      form.get('continuous')?.setValue(null, { emitEvent: false });
    }

    if (contentData.action === TrailerFormAction.ARRIVE && !contentData.departedByMistake && status) {
      switch (status) {
        case TrailerStatus.EMPTY:
          form.get('direction')?.setValue(TrailerDirection.OUTBOUND);
          break;
        case TrailerStatus.LOADED:
        case TrailerStatus.PARTIAL:
          form.get('direction')?.setValue(TrailerDirection.INBOUND);
          break;
        case TrailerStatus.PRE_COOLING:
          form.get('direction')?.reset();
          break;
        default:
          console.error(`Unknown trailer status ${status}`);
      }
    }

    const fields: FieldValidators[] = [];
    const trailerGroup = TrailerHelper.getArriveTrailerGroup(trailerType);

    if (status === TrailerStatus.EMPTY || status === TrailerStatus.PRE_COOLING) {
      fields.push(...TrailerHelper.shipmentFieldsOptional, ...TrailerHelper.sealFieldsOptional);
    }

    if (TrailerHelper.isReefer(trailerType)) {
      fields.push(...temperatureValidations);
    }

    if (status === TrailerStatus.LOADED) {
      // Make shipment and seal number required
      switch (trailerGroup) {
        case ArriveGroups.REEFERS:
          fields.push(...TrailerHelper.temperatureFields);
          fields.push(...shipmentValidations, ...sealValidations);
          break;
        case ArriveGroups.SIDE_FLAT_TANK:
          fields.push(...shipmentValidations, ...TrailerHelper.sealFieldsOptional);
          break;
        case ArriveGroups.VAN_CONT_OTOP_CURT_HOP:
          fields.push(...shipmentValidations, ...sealValidations);
          break;
        case ArriveGroups.LIVE:
          fields.push(...TrailerHelper.shipmentFieldsOptional, ...TrailerHelper.sealFieldsOptional);
          break;
      }
    }

    if (status === TrailerStatus.PARTIAL) {
      switch (trailerGroup) {
        case ArriveGroups.REEFERS:
          fields.push(...TrailerHelper.temperatureFields);
          fields.push(...TrailerHelper.shipmentFieldsOptional, ...TrailerHelper.sealFieldsOptional);
          break;
        case ArriveGroups.SIDE_FLAT_TANK:
        case ArriveGroups.VAN_CONT_OTOP_CURT_HOP:
        case ArriveGroups.LIVE:
          fields.push(...TrailerHelper.shipmentFieldsOptional, ...TrailerHelper.sealFieldsOptional);
          fields.push(...TrailerHelper.shipmentFieldsOptional, ...TrailerHelper.sealFieldsOptional);
          fields.push(...TrailerHelper.shipmentFieldsOptional, ...TrailerHelper.sealFieldsOptional);
          break;
      }
    }
    fields.forEach((field: FieldValidators) => {
      TrailerHelper.setValidators(form, field);
    });
  }

  /**
   * @deprecated firefly-1-9758-ui will be removed after feature flag expires
   */
  public static processSiteCapabilities(
    form: FormGroup,
    capabilities: SiteCapabilities,
    formAction: TrailerFormAction,
  ): void {
    if (!capabilities?.allowLocationEdit && formAction === TrailerFormAction.EDIT) {
      form.get('locationId')?.disable();
    }

    if (!capabilities?.isWeightFunctionEnabled) {
      form.get('trailerWeight')?.disable();
    }

    if (!capabilities?.storageAttribute) {
      form.get('storage')?.disable();
    }

    if (!capabilities?.inspection) {
      form.get('inspection')?.disable();
    }

    if (!capabilities?.allowFreightTypes) {
      form.get('freightTypeId')?.disable();
    }

    if (!capabilities?.shipmentDate) {
      form.get('shipmentDate')?.disable();
    }
  }

  private static runCheck(
    popupSvc: PopupService,
    trailer: Partial<Trailer>,
    getChecks: (trailer: Partial<Trailer>) => string[],
    submitFn: () => void,
  ): void {
    const checks = getChecks(trailer);
    from(checks)
      .pipe(
        concatMap((x) => {
          return popupSvc.dialog('global.warning', x).afterClosed();
        }),
        takeWhile((result) => result !== false, true),
        reduce((acc, value) => acc && value, true),
      )
      .subscribe((allConfirmed: boolean) => {
        if (allConfirmed) {
          submitFn();
        }
      });
  }

  private static getFuelCheck(trailer: Partial<Trailer>, mode: DialogMode): string[] {
    const checks = [];
    const acceptableFuelLevels = [
      TrailerFuelLevel.Fuel75Percent,
      TrailerFuelLevel.Fuel85Percent,
      TrailerFuelLevel.Full,
    ];
    if (TrailerHelper.isReefer(trailer.trailerType) && !acceptableFuelLevels.some((x) => x == trailer.fuelLevel)) {
      checks.push(`yardControl.confirm${mode}FuelLevel`);
    }
    return checks;
  }

  private static getStorageArriveCheck(trailer: Partial<Trailer>): string[] {
    const checks = [];
    if (trailer.storage) {
      checks.push('yardControl.confirmStorageArrive');
    }
    return checks;
  }

  private static getStorageDepartCheck(trailer: Partial<Trailer>): string[] {
    const checks = [];
    if (trailer.storage) {
      checks.push('yardControl.confirmStorageDepart');
    }
    return checks;
  }

  private static getFuelAndStorageChecks(trailer: Partial<Trailer>, mode: DialogMode): string[] {
    return [...TrailerHelper.getFuelCheck(trailer, mode), ...TrailerHelper.getStorageArriveCheck(trailer)];
  }
}
